import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { MessageCircleHeart, RefreshCw, Lightbulb, HelpCircle, ListChecks, Download, ArrowLeft, Sun, Brain, Leaf, Sparkles, ArrowRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { PrimaryBtn, Field, GREEN, RUST, GOLD, IVORY, ART_LOTUS, ART_ZODIAC } from "../components/StepFrame";

const TABS = {
    overview: ["your_situation", "what_you_described", "triggers"],
    mindset: ["thoughts_feelings", "reactions", "recurring_patterns", "daily_life_impact", "needs_values"],
    guidance: ["possible_interpretations", "what_remains_unknown", "next_steps", "reflection_question"],
};
const TAB_LIST = [["overview", "Overview"], ["astrology", "Astrology"], ["mindset", "Mindset"], ["guidance", "Guidance"]];
const TENTATIVE = new Set(["possible_interpretations", "what_remains_unknown"]);
const INSIGHTS = [
    [Sun, "Astrological Profile", "Your sun sign & a reflection", "astrology"],
    [Brain, "Emotional Patterns", "Your triggers and reactions", "mindset"],
    [Leaf, "Personalised Guidance", "Practical steps for a better you", "guidance"],
    [HelpCircle, "Open Questions", "What we can't yet know", "guidance"],
];
const SIGN_META = {
    Aries: ["Fire", "Courage, initiative, drive"], Taurus: ["Earth", "Steadiness, comfort, loyalty"], Gemini: ["Air", "Curiosity, communication, change"],
    Cancer: ["Water", "Emotions, intuition, nurturing"], Leo: ["Fire", "Creativity, confidence, leadership"], Virgo: ["Earth", "Analytical, practical, growth"],
    Libra: ["Air", "Balance, harmony, relationships"], Scorpio: ["Water", "Depth, intensity, transformation"], Sagittarius: ["Fire", "Meaning, freedom, optimism"],
    Capricorn: ["Earth", "Responsibility, ambition, patience"], Aquarius: ["Air", "Independence, ideas, community"], Pisces: ["Water", "Imagination, empathy, dreams"],
};

export default function Report() {
    const { t } = useApp();
    const nav = useNavigate();
    const [report, setReport] = useState(null);
    const [status, setStatus] = useState("loading");
    const [state, setState] = useState(null);
    const [tab, setTab] = useState("overview");
    const [astro, setAstro] = useState(null);

    const generate = useCallback(async () => {
        setStatus("generating");
        try {
            const { data } = await api.post("/report/generate");
            setReport(data.report); setStatus("ready");
        } catch (e) {
            if (e.response?.status === 400 || e.response?.status === 403) { setStatus("need_assessment"); }
            else { setStatus("error"); toast.error(apiErr(e.response?.data?.detail)); }
        }
    }, []);

    const load = useCallback(async () => {
        try {
            const [r, a, z] = await Promise.all([api.get("/report"), api.get("/assessment"), api.get("/astrology").catch(() => ({ data: {} }))]);
            setState(a.data); setAstro(z.data.astrology || null);
            if (r.data.report) { setReport(r.data.report); setStatus("ready"); return; }
            if (a.data.paid_unlocked && a.data.all_complete) { generate(); return; }
            setStatus("need_assessment");
        } catch (e) { setStatus("error"); }
    }, [generate]);

    useEffect(() => { load(); }, [load]);

    if (status === "loading" || status === "generating") {
        return <Center><Spinner />{status === "generating" && <p className="mt-4 text-sm text-charcoal/60">{t("report.generating")}</p>}</Center>;
    }
    if (status === "need_assessment") {
        return <div className="max-w-xl mx-auto px-4 py-20 text-center">
            <h1 className="text-3xl text-forest">{t("report.title")}</h1>
            <p className="mt-3 text-charcoal/70">{state && !state.paid_unlocked ? "Complete your payment and all 18 questions to unlock your report." : "Finish all 18 questions to generate your report."}</p>
            <PrimaryBtn className="mt-6" onClick={() => nav("/assessment")}>{t("pay.go_assessment")}</PrimaryBtn>
        </div>;
    }
    if (status === "error") {
        return <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="report-retry">
            <h1 className="text-3xl text-forest">{t("report.title")}</h1>
            <p className="mt-3 text-charcoal/75 leading-relaxed">{t("report.retry_body")}</p>
            <PrimaryBtn data-testid="report-retry-button" onClick={generate} className="mt-6"><RefreshCw size={16} /> {t("common.retry")}</PrimaryBtn>
        </div>;
    }

    const s = report?.sections || {};
    const keys = (TABS[tab] || []).filter((k) => s[k]);

    return (
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-6 sm:py-8" data-testid="report-page">
            <div className="flex items-center justify-between">
                <button onClick={() => nav("/profile")} className="p-1 -ml-1" style={{ color: GREEN }}><ArrowLeft size={20} strokeWidth={1.6} /></button>
                <div className="text-xl"><Logo /></div>
                <button data-testid="report-download-pdf" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium print:hidden"
                    style={{ border: `1px solid ${GREEN}26`, color: GREEN, background: "#FBF8F1" }}><Download size={13} /> Download PDF</button>
            </div>

            <h1 className="mt-8 font-serif text-3xl sm:text-4xl lg:text-[2.6rem] leading-tight max-w-lg" style={{ color: GREEN }}>Your Personal<br />Self-Discovery Report</h1>
            <p className="mt-2 text-sm sm:text-base max-w-md" style={{ color: "#4E5A54" }}>A deeper understanding of your mind, emotions and life patterns.</p>

            <div className="mt-7 inline-flex rounded-xl p-1 gap-1 flex-wrap print:hidden" style={{ background: "#ECE6D8" }}>
                {TAB_LIST.map(([k, l]) => (
                    <button key={k} data-testid={`report-tab-${k}`} onClick={() => setTab(k)} className="px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition"
                        style={tab === k ? { background: GREEN, color: IVORY } : { color: GREEN }}>{l}</button>
                ))}
            </div>

            {tab === "overview" && (
                <>
                    <div className="mt-6 rounded-2xl p-6 sm:p-7 grid md:grid-cols-[1fr_200px] gap-6 items-center" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }}>
                        <div>
                            <h2 className="font-serif text-xl sm:text-2xl" style={{ color: GREEN }}>Your Inner Self Snapshot</h2>
                            <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4E5A54" }}>{(s.your_situation || "").split("\n")[0]}</p>
                        </div>
                        <div className="hidden md:block rounded-xl overflow-hidden h-36"><img src={ART_LOTUS} alt="" className="w-full h-full object-cover object-right" /></div>
                    </div>
                    <h2 className="mt-10 font-serif text-xl sm:text-2xl" style={{ color: GREEN }}>Key Insights</h2>
                    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {INSIGHTS.map(([Icon, title, sub, to]) => (
                            <button key={title} data-testid={`insight-${to}-${title}`} onClick={() => setTab(to)} className="rounded-xl p-5 text-center flex flex-col items-center gap-2 transition hover:-translate-y-0.5"
                                style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1F` }}>
                                <Icon size={30} strokeWidth={1.3} style={{ color: RUST }} />
                                <span className="text-sm font-semibold" style={{ color: GREEN }}>{title}</span>
                                <span className="text-[11px] leading-snug" style={{ color: "#6B766F" }}>{sub}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}

            {tab === "astrology" && <Astrology astro={astro} onSaved={setAstro} stars={s.your_stars_your_story} />}

            {keys.length > 0 && (
                <div className="mt-8 space-y-4">
                    {keys.map((k, i) => <Section key={k} id={k} index={i} title={t(`report.sections.${k}`)} body={s[k]} tentative={TENTATIVE.has(k)} isSteps={k === "next_steps"} isQuestion={k === "reflection_question"} />)}
                </div>
            )}

            <div className="mt-10 rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden" style={{ background: GREEN }}>
                <div>
                    <h3 className="font-serif text-xl" style={{ color: IVORY }}>{t("report.open_companion")}</h3>
                    <p className="text-sm mt-1" style={{ color: `${IVORY}B3` }}>Your companion can use this report as context, with your consent.</p>
                </div>
                <button data-testid="report-open-companion" onClick={() => nav("/companion")} className="rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2 shrink-0 text-sm" style={{ background: GOLD, color: "#24210F" }}>
                    <MessageCircleHeart size={18} /> {t("nav.companion")}
                </button>
            </div>
        </div>
    );
}

const Astrology = ({ astro, onSaved, stars }) => {
    const [dob, setDob] = useState("");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { const { data } = await api.post("/astrology", { birth_date: dob }); onSaved(data.astrology); toast.success("Birth details saved."); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
        setBusy(false);
    };
    const [element, traits] = SIGN_META[astro?.sun_sign] || ["", ""];
    return (
        <div className="mt-6 grid lg:grid-cols-[1fr_260px] gap-6">
            <div>
                <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: GREEN }}>Your Astrological Insights</h2>
                <p className="mt-1 text-sm max-w-md" style={{ color: "#4E5A54" }}>A gentle lens on your patterns — offered as reflection, never as prediction.</p>
                {astro ? (
                    <>
                        <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
                            <div data-testid="astro-sun-sign" className="rounded-xl p-5 text-center" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1F` }}>
                                <Sun size={32} strokeWidth={1.3} className="mx-auto" style={{ color: RUST }} />
                                <p className="mt-2 text-xs" style={{ color: "#6B766F" }}>Sun Sign</p>
                                <p className="font-serif text-2xl" style={{ color: GREEN }}>{astro.sun_sign}</p>
                                <p className="text-[11px] mt-1" style={{ color: "#6B766F" }}>{traits}</p>
                            </div>
                            <div className="rounded-xl p-5 text-center" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1F` }}>
                                <Sparkles size={32} strokeWidth={1.3} className="mx-auto" style={{ color: RUST }} />
                                <p className="mt-2 text-xs" style={{ color: "#6B766F" }}>Element</p>
                                <p className="font-serif text-2xl" style={{ color: GREEN }}>{element}</p>
                                <p className="text-[11px] mt-1" style={{ color: "#6B766F" }}>Born {astro.birth_date}</p>
                            </div>
                        </div>
                        <div className="mt-5 rounded-2xl p-6" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }}>
                            <h3 className="font-serif text-xl" style={{ color: GREEN }}>A reflection for {astro.sun_sign}</h3>
                            <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4E5A54" }}>{astro.reflection}</p>
                        </div>
                        {stars && (
                            <div data-testid="report-section-your_stars_your_story" className="mt-4 rounded-2xl p-6" style={{ background: "#F6EEDC", border: `1px solid ${GOLD}66` }}>
                                <h3 className="font-serif text-xl" style={{ color: GREEN }}>Your Stars, Your Story</h3>
                                <p className="mt-2 text-sm leading-relaxed whitespace-pre-line" style={{ color: "#2A3A33" }}>{stars}</p>
                                <p className="mt-3 text-[11px] italic" style={{ color: "#6B766F" }}>A reflective lens, not a prediction or guarantee.</p>
                            </div>
                        )}
                        <p className="mt-4 text-xs" style={{ color: "#6B766F" }}>Moon and rising signs need your exact birth time and place — not collected yet, so we don't guess them.</p>
                    </>
                ) : (
                    <div className="mt-6 rounded-2xl p-6 max-w-md" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }}>
                        <Field data-testid="astro-dob" label="Date of Birth" type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                        <PrimaryBtn data-testid="astro-save" className="mt-4" onClick={save} disabled={!dob || busy}>Add Birth Details <ArrowRight size={15} /></PrimaryBtn>
                    </div>
                )}
            </div>
            <div className="hidden lg:block relative rounded-2xl overflow-hidden min-h-[380px]">
                <img src={ART_ZODIAC} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <p className="absolute bottom-6 left-6 right-6 font-serif italic text-lg leading-snug" style={{ color: IVORY }}>“The universe is always guiding you, even in your quiet moments.”</p>
            </div>
        </div>
    );
};

const Section = ({ id, index, title, body, tentative, isSteps, isQuestion }) => {
    const Icon = tentative ? (id === "what_remains_unknown" ? HelpCircle : Lightbulb) : (isSteps ? ListChecks : null);
    return (
        <div data-testid={`report-section-${id}`} className="rounded-2xl p-6 sm:p-7 animate-fade-up"
            style={{ animationDelay: `${index * 0.04}s`, background: isQuestion ? "#F6EEDC" : "#FBF8F1", border: `1px solid ${isQuestion ? `${GOLD}66` : `${GREEN}1A`}` }}>
            <div className="flex items-center gap-2">
                {Icon && <Icon size={16} style={{ color: RUST }} />}
                <h2 className="font-serif text-xl sm:text-2xl" style={{ color: GREEN }}>{title}</h2>
            </div>
            {tentative && <p className="text-xs mt-1 italic" style={{ color: "#6B766F" }}>A possibility to consider — not a fact or diagnosis.</p>}
            <div className="mt-3 leading-relaxed whitespace-pre-line text-[15px]" style={{ color: "#2A3A33" }}>{body}</div>
        </div>
    );
};

const Center = ({ children }) => <div className="min-h-[60vh] flex flex-col items-center justify-center">{children}</div>;
const Spinner = () => <div className="h-8 w-8 rounded-full border-2 border-forest/20 border-t-forest animate-spin" />;
